import { ComponentProps } from "react";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { cn } from "@util/helpers";

type MyAccountCardProps = {
  /** The article to display */
  article: ComponentProps<typeof Card>;
  /** Callback to handle delete article */
  handleDeleteArticle?: (articleId: string) => void;
  /** Indicator to display delete button */
  isDisplayDeleteButton?: boolean;
  /** Variant of the card */
  variant?: CardVariations;
  /** Optional additional styling for component */
  className?: string;
};

/**
 * MyAccount Card Component use in myBT and Reading History
 * Figma:
 * https://www.figma.com/design/soi1ZhMqZHbss6tJLvLgaa/MY-ACCOUNT--WEB-?node-id=3707-35581&t=1IQZnRIEwkg358qY-4
 * https://www.figma.com/design/soi1ZhMqZHbss6tJLvLgaa/MY-ACCOUNT--WEB-?node-id=3611-20579&t=XsTPoO6b0V5zVeJ1-4
 */
const MyAccountCard = ({
  article,
  handleDeleteArticle,
  isDisplayDeleteButton,
  variant,
  className,
}: MyAccountCardProps): React.ReactElement => {
  const _id = article.id.toString();
  const handleDelete = () => {
    if (!handleDeleteArticle) return;

    handleDeleteArticle(_id);
  };

  const commonVariant: CardVariations = {
    image: {
      width: "w-1/4",
      position: "right",
      aspectRatio: "aspect-3x2",
      orientation: "landscape",
      filters: { w: 900, h: 600, dpr: 1, f: "webp" },
    },
    content: {
      width: "w-3/4",
      extraClass: "pr-4",
      blurb: { extraClass: "text-gray-850", wrapperClassName: cn("lg:my-2") },
      updatedTimeColor: cn("text-gray-850"),
      updatedTimeExtraClass: cn("font-poppins text-7xs"),
      updatedTimeWrapperClass: cn("mt-2"),
    },
    title: {
      size: "text-base",
      color: "text-gray-850",
      extraClass: cn("font-bold font-lct"),
    },
    subscriberLabel: { extraClass: "mt-2 mb-0" },
  };

  return (
    <div
      className={cn(
        "flex justify-between border-b border-gray-175 py-4",
        className
      )}
      data-testid={`reading-history-basic-card-${_id}`}
    >
      <Card {...article} variations={variant || commonVariant} />

      {isDisplayDeleteButton ? (
        <button
          onClick={handleDelete}
          className="ml-6 flex-none text-gray-515"
          data-testid="reading-history-delete-button"
        >
          <IconFactory type={IconTypeEnum.close} size={24} />
        </button>
      ) : null}
    </div>
  );
};

export default MyAccountCard;
